import { memo } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { LibrariesSubSideNav } from '~/components/v2/components/workspace/libraries-nav/LibrariesSubSideNav';
import { SettingsSubSideNav } from '~/components/v2/components/workspace/settings-nav/SettingsSubSideNav';
import { SubSideNav } from '~/components/v2/components/workspace/sub-side-nav/SubSideNav';
import { useSubSideNavPathname } from '~/components/v2/components/workspace/sub-side-nav/useSubSideNavPathname';
import { useSubSideNavStore } from '~/components/v2/store/features/workspace/useSubSideNavStore';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { isLibrariesSubSideNavPathname } from '~/utils/isLibrariesSubSideNavPathname';
import { isSearchSubSideNavPathname } from '~/utils/isSearchSubSideNavPathname';
import { isSettingsSubSideNavPathname } from '~/utils/isSettingsSubSideNavPathname';
import { isSubSideNavOpen } from '~/utils/isSubSideNavOpen';
import { isSubSideNavPathname } from '~/utils/isSubSideNavPathname';
import {
  SIDEBAR_WIDTH_VAR,
  setSubSideNavWidth,
  subscribeSubSideNavWidthSync,
} from '~/utils/subscribeSubSideNavWidthSync';

const getSubSideNavLabel = ({
  isLibrariesActive,
  isSettingsActive,
  libraries,
}: {
  isLibrariesActive: boolean;
  isSettingsActive: boolean;
  libraries: boolean | undefined;
}) => {
  if (isSettingsActive) {
    return 'Settings';
  }

  if (isLibrariesActive && libraries) {
    return 'Libraries';
  }

  if (isLibrariesActive) {
    return 'Boards';
  }

  return 'Libraries';
};

export const WorkspaceSubSideNav = memo(() => {
  const { subSideNavPathname: pathname } = useSubSideNavPathname();
  const { libraries } = usePrivateAddOnFeatures();
  const isSubNavActive = isSubSideNavPathname(pathname);
  const isLibrariesPanelActive = isSearchSubSideNavPathname(pathname) || isLibrariesSubSideNavPathname(pathname);
  const isSettingsActive = isSettingsSubSideNavPathname(pathname);
  const storeOpen = useSubSideNavStore((state) => state.open);
  const isResizing = useSubSideNavStore((state) => state.isResizing);
  const open = isSubSideNavOpen(pathname, storeOpen);
  const label = getSubSideNavLabel({
    isLibrariesActive: isLibrariesPanelActive,
    isSettingsActive,
    libraries,
  });

  useIsomorphicLayoutEffect(() => {
    subscribeSubSideNavWidthSync();
    setSubSideNavWidth(open, isResizing);
  }, [isResizing, open]);

  return (
    <SubSideNav
      active={isSubNavActive}
      collapsible={!isSettingsActive}
      label={label}
      style={{ width: `var(${SIDEBAR_WIDTH_VAR}, 0px)` }}
    >
      {isLibrariesPanelActive ? <LibrariesSubSideNav /> : null}
      {isSettingsActive ? <SettingsSubSideNav /> : null}
    </SubSideNav>
  );
});

WorkspaceSubSideNav.displayName = 'WorkspaceSubSideNav';
